/** Eight certificate-depth examples per part for Descriptive Statistics. */

export const PS_DESC_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Mean, median, and mode together",
    setup: "Data: $4,7,7,9,12,15,16$. Find all three measures of centre.",
    steps: [
      { text: "The data are already sorted, which the median needs.", why: "Always sort first." },
      { text: "Sum $=4+7+7+9+12+15+16=70$.", why: "Numerator of the mean." },
      { text: "$n=7$, so $\\bar{x}=70/7=10$.", why: "Definition of the mean." },
      { text: "The median is the $\\left(\\tfrac{n+1}{2}\\right)$th $=4$th value.", why: "Odd sample size." },
      { text: "That value is $9$.", why: "Count in from either end." },
      { text: "The mode is $7$, the only repeated value.", why: "Highest frequency." },
      { text: "Order: mode $7<$ median $9<$ mean $10$.", why: "Typical of right-skewed data." },
      { text: "The two large values $15,16$ pull the mean above the median.", why: "The mean is sensitive to the tail." },
    ],
    result: "$\\bar{x}=10$, median $=9$, mode $=7$",
    check: "Deviations from the mean sum to zero: $-6-3-3-1+2+5+6=0$.",
    mistake: "Reporting the median without sorting the data first.",
    life: "Salary reports quote medians rather than means precisely because a few very high earners drag the mean upward, exactly as here.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Median for an even sample size",
    setup: "Data: $3,8,10,14,18,25$. Find the median and the effect of adding an outlier $90$.",
    steps: [
      { text: "$n=6$ is even, so average the two middle values.", why: "Even-size rule." },
      { text: "The middle values are the $3$rd and $4$th: $10$ and $14$.", why: "Positions $n/2$ and $n/2+1$." },
      { text: "Median $=\\dfrac{10+14}{2}=12$.", why: "Average them." },
      { text: "Mean $=\\dfrac{3+8+10+14+18+25}{6}=\\dfrac{78}{6}=13$.", why: "For comparison." },
      { text: "Now append $90$: $n=7$ and the median is the $4$th value $=14$.", why: "Recount after adding a point." },
      { text: "The median moved only from $12$ to $14$.", why: "Robustness of the median." },
      { text: "New mean $=\\dfrac{78+90}{7}=24$.", why: "Recompute." },
      { text: "The mean nearly doubled while the median barely moved.", why: "Quantifies why medians resist outliers." },
    ],
    result: "Median $12$ (mean $13$); after the outlier, median $14$ but mean $24$",
    check: "The outlier is more than three interquartile ranges above $Q_3$, so it is genuinely extreme.",
    mistake: "Taking the third value alone as the median when $n$ is even.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Mean from a frequency table",
    setup: "Scores $1,2,3,4,5$ occur $2,5,8,3,2$ times. Find the mean and the modal class.",
    steps: [
      { text: "Total frequency $n=2+5+8+3+2=20$.", why: "Denominator." },
      { text: "Products $xf$: $1(2)=2$, $2(5)=10$.", why: "Weighted contributions." },
      { text: "$3(8)=24$, $4(3)=12$, $5(2)=10$.", why: "Continue." },
      { text: "$\\sum xf=2+10+24+12+10=58$.", why: "Numerator." },
      { text: "$\\bar{x}=\\dfrac{58}{20}=2.9$.", why: "Weighted mean formula." },
      { text: "Mode is $3$, the score with the highest frequency $8$.", why: "Read the table." },
      { text: "Cumulative frequencies: $2,7,15,18,20$; the $10$th and $11$th values both fall in score $3$.", why: "Locate the median." },
      { text: "So median $=3$, slightly above the mean $2.9$.", why: "Mild left skew." },
    ],
    result: "$\\bar{x}=2.9$, median $=3$, mode $=3$",
    check: "$\\sum f=20$ matches the stated sample size.",
    mistake: "Averaging the five distinct scores to get $3$, ignoring the frequencies.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Quartiles and the interquartile range",
    setup: "Data: $2,4,4,5,7,8,9,12,15,18$. Find $Q_1$, $Q_2$, $Q_3$, and the IQR.",
    steps: [
      { text: "$n=10$, sorted already.", why: "Prerequisite." },
      { text: "$Q_2$ averages the $5$th and $6$th values: $\\dfrac{7+8}{2}=7.5$.", why: "Median of an even sample." },
      { text: "Lower half: $2,4,4,5,7$ (five values).", why: "Split at the median." },
      { text: "$Q_1$ is the middle of that half: $4$.", why: "Third value of five." },
      { text: "Upper half: $8,9,12,15,18$.", why: "Second half." },
      { text: "$Q_3$ is its middle: $12$.", why: "Third value of five." },
      { text: "$\\mathrm{IQR}=Q_3-Q_1=12-4=8$.", why: "Spread of the middle $50\\%$." },
      { text: "Outlier fences: $Q_1-1.5(8)=-8$ and $Q_3+1.5(8)=24$; no value falls outside.", why: "Standard outlier rule." },
    ],
    result: "$Q_1=4$, $Q_2=7.5$, $Q_3=12$, $\\mathrm{IQR}=8$, no outliers",
    check: "Half the data ($5$ values) lie between $4$ and $12$, as the IQR promises.",
    mistake: "Including the median in both halves when $n$ is even, which shifts the quartiles.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Percentile of a value and value at a percentile",
    setup: "For $10,12,15,15,18,20,22,25,30,35$, find the percentile rank of $20$ and the $30$th percentile.",
    steps: [
      { text: "$n=10$ and the data are sorted.", why: "Setup." },
      { text: "Values below $20$: $10,12,15,15,18$ — five of them.", why: "Count strictly below." },
      { text: "Percentile rank $=\\dfrac{\\text{below}+0.5(\\text{equal})}{n}\\times100=\\dfrac{5+0.5}{10}(100)$.", why: "Standard mid-rank convention." },
      { text: "$=55$th percentile.", why: "Compute." },
      { text: "For $P_{30}$, position $=0.30(n+1)=0.30(11)=3.3$.", why: "Interpolation formula." },
      { text: "The $3$rd value is $15$ and the $4$th is $15$.", why: "Both endpoints of the interval." },
      { text: "Interpolate: $15+0.3(15-15)=15$.", why: "Ties make the interpolation trivial." },
      { text: "So $P_{30}=15$, meaning $30\\%$ of the data lie at or below $15$.", why: "Interpretation." },
    ],
    result: "The value $20$ is at the $55$th percentile; $P_{30}=15$",
    check: "$P_{50}$ from the same formula is position $5.5$, giving $(18+20)/2=19$ — the median.",
    mistake: "Using position $0.30n=3$ instead of $0.30(n+1)$, which shifts every percentile.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Weighted mean with unequal weights",
    setup: "A course grades assignments $20\\%$, midterm $30\\%$, final $50\\%$. A student scores $85$, $72$, $91$. Find the course mark and the score needed on a resit final for $85$ overall.",
    steps: [
      { text: "Weighted mean $=\\sum w_ix_i$ with weights summing to $1$.", why: "Definition." },
      { text: "Assignments contribute $0.20(85)=17$.", why: "Term by term." },
      { text: "Midterm contributes $0.30(72)=21.6$.", why: "Second term." },
      { text: "Final contributes $0.50(91)=45.5$.", why: "Third term." },
      { text: "Total $=17+21.6+45.5=84.1$.", why: "Add." },
      { text: "For an $85$ target: $17+21.6+0.5f=85$.", why: "Keep the fixed parts, solve for the final." },
      { text: "$0.5f=46.4$, so $f=92.8$.", why: "Divide." },
      { text: "The unweighted average $\\left(85+72+91\\right)/3=82.7$ differs because the final carries half the weight.", why: "Shows the effect of weighting." },
    ],
    result: "Course mark $84.1$; a final of $92.8$ would be needed for an $85$ overall",
    check: "Weights $0.2+0.3+0.5=1$, so the result must lie between $72$ and $91$.",
    mistake: "Averaging the three scores equally and ignoring the syllabus weights.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Combining two group means",
    setup: "Class A has $30$ students averaging $68$; class B has $20$ averaging $78$. Find the combined mean.",
    steps: [
      { text: "Group totals: $\\text{sum}_A=n_A\\bar{x}_A=30(68)=2040$.", why: "Recover the sum from the mean." },
      { text: "$\\text{sum}_B=20(78)=1560$.", why: "Same for the second group." },
      { text: "Combined sum $=2040+1560=3600$.", why: "Sums add; means do not." },
      { text: "Combined $n=30+20=50$.", why: "Total count." },
      { text: "Combined mean $=3600/50=72$.", why: "Definition." },
      { text: "Compare the naive average of means: $(68+78)/2=73$.", why: "Shows the bias from ignoring sizes." },
      { text: "The true value is pulled toward $68$ because group A is larger.", why: "Weighted average reasoning." },
      { text: "Equivalently, $72=\\tfrac{30}{50}(68)+\\tfrac{20}{50}(78)=0.6(68)+0.4(78)$.", why: "Weights are the sample proportions." },
    ],
    result: "Combined mean $=72$",
    check: "$0.6(68)+0.4(78)=40.8+31.2=72$.",
    mistake: "Averaging the two means to get $73$ when the groups differ in size.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Effect of a linear transformation on centre",
    setup: "Temperatures in Celsius have mean $22$, median $21$, mode $19$. Convert to Fahrenheit with $F=1.8C+32$.",
    steps: [
      { text: "Linear transformations move every data point the same way, so order is preserved.", why: "The slope $1.8>0$." },
      { text: "Mean: $E[1.8C+32]=1.8E[C]+32$.", why: "Linearity of the mean." },
      { text: "$=1.8(22)+32=39.6+32=71.6^\\circ$F.", why: "Substitute." },
      { text: "Median transforms the same way because order is preserved: $1.8(21)+32=69.8^\\circ$F.", why: "Quantiles are equivariant under increasing maps." },
      { text: "Mode: $1.8(19)+32=66.2^\\circ$F.", why: "The most frequent value simply maps across." },
      { text: "All three keep their original ordering (mode $<$ median $<$ mean).", why: "Positive slope preserves ranking." },
      { text: "If the slope were negative, the median would still map directly but the ordering would reverse.", why: "Important caveat." },
      { text: "Note the additive $32$ shifts centre but will not affect spread measures in Part 2.", why: "Preview of variance behaviour." },
    ],
    result: "Mean $71.6^\\circ$F, median $69.8^\\circ$F, mode $66.2^\\circ$F",
    check: "Converting $71.6^\\circ$F back: $(71.6-32)/1.8=22^\\circ$C.",
    mistake: "Applying only the multiplier and forgetting the $+32$ offset.",
  },
];

export const PS_DESC_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Sample variance and standard deviation",
    setup: "Data: $4,8,6,10,12$. Compute $s^{2}$ and $s$.",
    steps: [
      { text: "$\\bar{x}=\\dfrac{4+8+6+10+12}{5}=\\dfrac{40}{5}=8$.", why: "Start with the mean." },
      { text: "Deviations: $-4,0,-2,2,4$.", why: "Each $x_i-\\bar{x}$." },
      { text: "They sum to zero, a useful arithmetic check.", why: "Always true for the mean." },
      { text: "Squared deviations: $16,0,4,4,16$.", why: "Squares remove signs." },
      { text: "Sum of squares $=40$.", why: "Numerator." },
      { text: "Sample variance divides by $n-1=4$: $s^{2}=40/4=10$.", why: "Bessel's correction for a sample." },
      { text: "$s=\\sqrt{10}\\approx3.162$.", why: "Back to the original units." },
      { text: "Population variance would be $40/5=8$ with $\\sigma\\approx2.83$.", why: "Shows the size of the correction." },
    ],
    result: "$s^{2}=10$ and $s\\approx3.16$",
    check: "Computational formula: $\\sum x^{2}=360$, $360-40^{2}/5=40$. Same numerator.",
    mistake: "Dividing by $n$ when the data are a sample rather than a whole population.",
    life: "Manufacturing tolerances are quoted in standard deviations; halving $s$ typically halves the scrap rate, which is why process control targets spread and not just the mean.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "z-scores compare different scales",
    setup: "Ali scored $78$ on a test with $\\mu=70,\\sigma=8$; Sara scored $85$ where $\\mu=80,\\sigma=10$. Who did better relatively?",
    steps: [
      { text: "A z-score measures distance from the mean in standard deviations: $z=\\dfrac{x-\\mu}{\\sigma}$.", why: "Makes different scales comparable." },
      { text: "Ali: $z=\\dfrac{78-70}{8}=1.0$.", why: "Substitute." },
      { text: "Sara: $z=\\dfrac{85-80}{10}=0.5$.", why: "Second student." },
      { text: "Ali is one full standard deviation above his mean; Sara is half of one.", why: "Interpretation." },
      { text: "So Ali performed better relative to his group despite the lower raw score.", why: "Answers the question." },
      { text: "If both distributions were normal, Ali is at the $84$th percentile.", why: "$\\Phi(1)\\approx0.84$." },
      { text: "Sara sits near the $69$th percentile.", why: "$\\Phi(0.5)\\approx0.69$." },
      { text: "Raw-score comparison ($85>78$) reverses the conclusion, which is why standardization matters.", why: "The core lesson." },
    ],
    result: "Ali ($z=1.0$) outperformed Sara ($z=0.5$) relative to their groups",
    check: "Ali's score is $8$ above the mean and $\\sigma=8$, so $z=1$ exactly.",
    mistake: "Comparing raw scores across tests with different means and spreads.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Coefficient of variation",
    setup: "Machine A produces parts with $\\mu=50$ mm, $\\sigma=2$ mm; machine B has $\\mu=200$ mm, $\\sigma=6$ mm. Which is more consistent?",
    steps: [
      { text: "Absolute spread favours A: $2<6$.", why: "But the scales differ." },
      { text: "The coefficient of variation is $\\mathrm{CV}=\\dfrac{\\sigma}{\\mu}\\times100\\%$.", why: "Relative variability." },
      { text: "Machine A: $\\mathrm{CV}=\\dfrac{2}{50}(100)=4\\%$.", why: "Substitute." },
      { text: "Machine B: $\\mathrm{CV}=\\dfrac{6}{200}(100)=3\\%$.", why: "Second machine." },
      { text: "Relative to what it produces, B is more consistent.", why: "Lower CV." },
      { text: "A part $1\\sigma$ off target is $4\\%$ off spec for A but $3\\%$ for B.", why: "Practical restatement." },
      { text: "CV is only meaningful for positive-mean ratio data.", why: "Important limitation." },
      { text: "For tolerance bands quoted in percent, B meets a $\\pm5\\%$ spec more comfortably.", why: "Engineering conclusion." },
    ],
    result: "Machine B is relatively more consistent ($\\mathrm{CV}=3\\%$ versus $4\\%$)",
    check: "Scaling all of A's measurements by $4$ would give $\\mu=200,\\sigma=8$ — still a $4\\%$ CV.",
    mistake: "Comparing raw standard deviations when the means differ by a factor of four.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Variance from a frequency table",
    setup: "Values $2,4,6,8$ with frequencies $3,5,4,2$. Find $\\bar{x}$ and $s^{2}$.",
    steps: [
      { text: "$n=3+5+4+2=14$.", why: "Total count." },
      { text: "$\\sum xf=6+20+24+16=66$.", why: "Weighted sum." },
      { text: "$\\bar{x}=66/14\\approx4.714$.", why: "Weighted mean." },
      { text: "$\\sum x^{2}f=4(3)+16(5)+36(4)+64(2)$.", why: "Second moment." },
      { text: "$=12+80+144+128=364$.", why: "Add." },
      { text: "Computational formula: $\\sum x^{2}f-\\dfrac{(\\sum xf)^{2}}{n}=364-\\dfrac{4356}{14}$.", why: "Avoids rounding the mean." },
      { text: "$=364-311.143=52.857$.", why: "Corrected sum of squares." },
      { text: "$s^{2}=\\dfrac{52.857}{13}\\approx4.066$, so $s\\approx2.016$.", why: "Divide by $n-1$." },
    ],
    result: "$\\bar{x}\\approx4.71$, $s^{2}\\approx4.07$, $s\\approx2.02$",
    check: "All data lie in $[2,8]$, and $\\bar{x}\\pm2s\\approx[0.7,8.7]$ comfortably contains them.",
    mistake: "Forgetting to weight the squares by the frequencies.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Boxplot and outlier detection",
    setup: "Data: $12,14,15,16,18,19,21,22,24,45$. Build the five-number summary and test for outliers.",
    steps: [
      { text: "Sorted with $n=10$; minimum $12$, maximum $45$.", why: "Ends of the summary." },
      { text: "Median averages the $5$th and $6$th: $\\dfrac{18+19}{2}=18.5$.", why: "Even sample." },
      { text: "Lower half $12,14,15,16,18$ gives $Q_1=15$.", why: "Middle of five." },
      { text: "Upper half $19,21,22,24,45$ gives $Q_3=22$.", why: "Middle of five." },
      { text: "$\\mathrm{IQR}=22-15=7$.", why: "Box width." },
      { text: "Fences: $15-1.5(7)=4.5$ and $22+1.5(7)=32.5$.", why: "$1.5\\times\\mathrm{IQR}$ rule." },
      { text: "$45>32.5$, so $45$ is an outlier; it is even beyond $Q_3+3\\,\\mathrm{IQR}=43$, making it extreme.", why: "Two-tier classification." },
      { text: "Whisker ends are then $12$ and $24$, with $45$ plotted as an isolated point.", why: "Correct boxplot construction." },
    ],
    result: "Five-number summary $12,15,18.5,22,24$ with $45$ flagged as an extreme outlier",
    check: "The mean $20.6$ exceeds the median $18.5$ — the outlier's fingerprint.",
    mistake: "Drawing the whisker out to $45$ instead of to the largest non-outlier value.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Chebyshev versus the empirical rule",
    setup: "A distribution has $\\mu=100$, $\\sigma=15$. Bound $P(70<X<130)$ with and without a normality assumption.",
    steps: [
      { text: "The interval is $100\\pm30$, i.e. $k=30/15=2$ standard deviations.", why: "Convert to $k$." },
      { text: "Chebyshev: $P(|X-\\mu|<k\\sigma)\\ge1-\\dfrac{1}{k^{2}}$.", why: "Holds for any distribution." },
      { text: "$1-\\tfrac14=0.75$, so at least $75\\%$ of values lie in the interval.", why: "Distribution-free bound." },
      { text: "If the data are normal, the empirical rule gives about $95\\%$.", why: "Much sharper under an assumption." },
      { text: "The exact normal value is $2\\Phi(2)-1=0.9545$.", why: "From the standard normal table." },
      { text: "Chebyshev's $75\\%$ is a guarantee, not an estimate.", why: "Interpretation of a bound." },
      { text: "For $k=3$: Chebyshev gives $\\ge88.9\\%$ while normality gives $99.7\\%$.", why: "The gap widens." },
      { text: "Use Chebyshev when the shape is unknown or clearly skewed; use the empirical rule only for roughly bell-shaped data.", why: "Practical rule of thumb." },
    ],
    result: "At least $75\\%$ by Chebyshev; about $95.5\\%$ if the distribution is normal",
    check: "Chebyshev says nothing for $k=1$ ($1-1=0$), correctly reflecting that it is a weak universal bound.",
    mistake: "Quoting $95\\%$ for a badly skewed distribution where only $75\\%$ is guaranteed.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Standard deviation under a linear change",
    setup: "A dataset has $\\bar{x}=40$, $s=5$. Find the mean and standard deviation of $y=3x-10$, and the z-score of $x=50$ in both scales.",
    steps: [
      { text: "Mean transforms linearly: $\\bar{y}=3(40)-10=110$.", why: "Both operations affect the centre." },
      { text: "Adding a constant shifts data without changing spread.", why: "The $-10$ is irrelevant to $s$." },
      { text: "Multiplying scales spread by the absolute value: $s_y=|3|(5)=15$.", why: "Standard deviation is homogeneous of degree $1$." },
      { text: "Variance scales by the square: $s_y^{2}=9(25)=225$.", why: "Consistent with $15^{2}$." },
      { text: "z-score of $x=50$: $\\dfrac{50-40}{5}=2$.", why: "Original scale." },
      { text: "The matching $y$ value is $3(50)-10=140$.", why: "Transform the point too." },
      { text: "Its z-score: $\\dfrac{140-110}{15}=2$.", why: "Identical." },
      { text: "z-scores are invariant under positive linear transformations — which is exactly why they are used for comparison.", why: "Structural conclusion." },
    ],
    result: "$\\bar{y}=110$, $s_y=15$, and the z-score stays $2$ in both scales",
    check: "$\\mathrm{CV}$ changes ($12.5\\%$ to $13.6\\%$) because the shift moves the mean but not the spread.",
    mistake: "Scaling the standard deviation by $9$ (the variance factor) instead of $3$.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Skewness from the summary statistics",
    setup: "For $3,4,4,5,5,5,6,7,20$, compute the mean, median, and Pearson's skewness coefficient, and decide which centre to report.",
    steps: [
      { text: "$n=9$; sum $=3+4+4+5+5+5+6+7+20=59$.", why: "Setup." },
      { text: "$\\bar{x}=59/9\\approx6.556$.", why: "Mean." },
      { text: "Median is the $5$th value $=5$; mode is $5$.", why: "Sorted data." },
      { text: "$\\sum x^{2}=9+16+16+25+25+25+36+49+400=601$.", why: "For the variance." },
      { text: "Corrected sum of squares $=601-\\dfrac{59^{2}}{9}=601-386.78=214.22$.", why: "Computational formula." },
      { text: "$s^{2}=214.22/8=26.78$, so $s\\approx5.175$.", why: "Sample variance." },
      { text: "Pearson's second coefficient: $\\dfrac{3(\\bar{x}-\\text{median})}{s}=\\dfrac{3(1.556)}{5.175}\\approx0.90$.", why: "Positive means right-skewed." },
      { text: "With strong right skew driven by the value $20$, the median $5$ is the honest summary.", why: "Reporting decision." },
    ],
    result: "$\\bar{x}\\approx6.56$, median $=5$, skewness $\\approx+0.90$ — report the median",
    check: "Removing the $20$ gives mean $4.875$ and median $5$, nearly symmetric.",
    mistake: "Reporting the mean alone for visibly skewed data, which overstates the typical value.",
  },
];
